import * as _ from 'lodash';
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { type AlertDialogProps, type DialogItemProps, useDialogContext } from '../../../contexts/DialogContext';
import useGetMalwareReports from '../../../hooks/api/malware/useGetMalwareReports';
import Loader from '../../generic/Loader';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import SimpleReactTable from '../../generic/SimpleReactTable';
import TableHeader from '../../generic/TableHeader';
import type { MalwareReport } from '../../../../shared/model-typings/malwarereport';

import { ActionsCell, LocatorsCell, UrlCell } from './MalwareTableCells';

const getColumns = (
  onEditMalware: (malwareReport: MalwareReport) => void,
  onShowLocators: (dialogItem: DialogItemProps<AlertDialogProps>) => void,
) => [
  {
    id: 'name',
    sortable: false,
    Header: <TableHeader title="Name" />,
    accessor: 'name',
    resizable: true,
    style: {
      height: 'auto',
    },
  },
  {
    id: 'url',
    sortable: false,
    Header: <TableHeader title="URL" />,
    accessor: 'url',
    Cell: UrlCell,
    width: 250,
    resizable: true,
    style: {
      height: 'auto',
    },
  },
  {
    id: 'description',
    sortable: false,
    Header: <TableHeader title="Description" />,
    accessor: 'description',
    resizable: true,
    width: 200,
    style: {
      whiteSpace: 'pre-line',
      height: 'auto',
    },
  },
  {
    id: 'userId',
    sortable: false,
    Header: <TableHeader title="User ID" />,
    accessor: 'userId',
    resizable: true,
    width: 75,
    style: {
      height: 'auto',
    },
  },
  {
    id: 'userEmail',
    sortable: false,
    Header: <TableHeader title="User Email" />,
    accessor: 'userEmail',
    resizable: true,
    style: {
      height: 'auto',
    },
  },
  {
    id: 'locators',
    sortable: false,
    Header: <TableHeader title="Locators" />,
    accessor: 'malwareRevisions',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Cell: ({ row }: { row: { _original: MalwareReport } }) => (
      <LocatorsCell row={row} onShowLocators={onShowLocators} />
    ),
    resizable: true,
    width: 350,
    style: {
      height: 'auto',
    },
  },
  {
    id: 'createdAt',
    sortable: false,
    Header: <TableHeader title="Created At" />,
    accessor: 'createdAt',
    resizable: true,
    style: {
      height: 'auto',
    },
  },
  {
    id: 'actions',
    sortable: false,
    Header: <TableHeader title="Actions" />,
    accessor: 'id',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Cell: ({ row }: { row: { _original: MalwareReport } }) => (
      <ActionsCell row={row} onEditMalware={onEditMalware} />
    ),
    width: 100,
    resizable: false,
    style: {
      height: 'auto',
    },
  },
];

type MalwareReportsTableProps = {
  onEditMalware: (malwareReport: MalwareReport) => void;
};

const MalwareReportsTable: VoidFunctionComponent<MalwareReportsTableProps> = ({
  onEditMalware,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { onAlert: onShowLocators } = useDialogContext();

  const {
    data,
    isLoading,
    error,
  } = useGetMalwareReports(currentPage, pageSize);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <>Failed to load Malware Reports</>;
  }

  const results = data?.results ?? [];
  const columns = getColumns(onEditMalware, onShowLocators);

  return (
    <>
      <SimpleReactTable
        data={results}
        columns={columns}
        onSortedChanged={_.noop}
        defaultSortMode={[{ id: 'id', desc: false }]}
        showPagination={false}
        isLoading={isLoading}
      />

      {data && (
        <PaginationFooter
          page={currentPage}
          count={pageSize}
          totalItems={data.totalCount}
          setPage={setCurrentPage}
          setCount={setPageSize}
        />
      )}
    </>
  );
};

export default MalwareReportsTable;
